"use strict";
/**
 * Display-orderbook contract and builder shared by server routes and the
 * client renderer.
 *
 * The orders-page widget shows, per column (yes-bid / yes-ask / no-bid /
 * no-ask): the TRUE best price (never rounded) plus up to 3 cent buckets with
 * running cumulative dollar liquidity. Bucketing rounds away from the mid so
 * a bucket's price is the worst price a taker would pay/receive for that
 * volume: asks round UP to the next cent, bids round DOWN.
 *
 * Pure module: no DOM, no Node APIs, no imports from src/server or
 * src/client — it is bundled into both.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_DISPLAY_LEVELS = void 0;
exports.buildDisplayOrderbook = buildDisplayOrderbook;
const price_math_1 = require("./price-math");
exports.MAX_DISPLAY_LEVELS = 3;
const EMPTY_SIDE = () => ({ best: null, levels: [] });
/**
 * Build one display column from raw levels.
 * Bid columns sort descending and bucket down; ask columns sort ascending
 * and bucket up. Dollar liquidity accumulates at this column's own price
 * (callers pass pre-inverted levels for derived columns).
 */
function buildColumn(levels, kind) {
    const sorted = levels
        .filter(([, qty]) => qty > 0)
        .sort((a, b) => (kind === "bid" ? b[0] - a[0] : a[0] - b[0]));
    if (sorted.length === 0) {
        return EMPTY_SIDE();
    }
    const bucketOf = kind === "bid" ? price_math_1.centBucketDownBps : price_math_1.centBucketUpBps;
    const buckets = [];
    let cumulative = 0;
    let currentBucket = null;
    for (const [bps, qty] of sorted) {
        const bucket = bucketOf(bps);
        if (currentBucket === null) {
            currentBucket = bucket;
        }
        else if (bucket !== currentBucket) {
            buckets.push({
                price: (0, price_math_1.bpsToDollarString)(currentBucket),
                dollars: cumulative,
            });
            if (buckets.length >= exports.MAX_DISPLAY_LEVELS) {
                currentBucket = null;
                break;
            }
            currentBucket = bucket;
        }
        cumulative += (bps / 10000) * qty;
    }
    if (currentBucket !== null && buckets.length < exports.MAX_DISPLAY_LEVELS) {
        buckets.push({
            price: (0, price_math_1.bpsToDollarString)(currentBucket),
            dollars: cumulative,
        });
    }
    return { best: (0, price_math_1.bpsToDollarString)(sorted[0][0]), levels: buckets };
}
/**
 * Build all four display columns from a YES-quoted book.
 * Kalshi: yesBids from yes-side bids, yesAsks from inverted no-side bids.
 * PM-US: yesBids/yesAsks directly from the YES-token book.
 */
function buildDisplayOrderbook(input) {
    const inverted = (levels) => levels.map(([bps, qty]) => [(0, price_math_1.invertBps)(bps), qty]);
    return {
        yesBid: buildColumn(input.yesBids, "bid"),
        noAsk: buildColumn(inverted(input.yesBids), "ask"),
        yesAsk: buildColumn(input.yesAsks, "ask"),
        noBid: buildColumn(inverted(input.yesAsks), "bid"),
    };
}
